// src/utils/authAxios.js
import axios from 'axios';
import { shouldUseMsal, loginRequest, getSharedMsalInstance } from '../config/msalConfig';
import { isEmbedMode, getEmbedToken } from './embedAuth';

let tokenPromise = null;

/** App Service often returns 403 for /.auth/refresh; skip further attempts this session (reduces console noise). */
const AUTH_REFRESH_SKIP_KEY = 'thevault_skip_auth_refresh';

// Get MSAL instance if available - use shared instance from msalConfig
const getMsalInstance = async () => {
  if (!shouldUseMsal()) return null;
  return await getSharedMsalInstance();
};

// Fetch an Easy Auth token if available. Optional: proceed without if unavailable.
const fetchEasyAuthToken = async () => {
  try {
    console.log('[authAxios] Attempting to fetch Easy Auth token...');
    
    // App Service: /.auth/refresh may be disabled (returns 403); this is expected and harmless.
    // Skip after first 403/404 for the tab session to avoid repeated failed requests in DevTools.
    // Same-origin API calls use cookies; tokens are optional.
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(AUTH_REFRESH_SKIP_KEY) === '1') {
        // already know refresh is unavailable
      } else {
        const r = await fetch('/.auth/refresh', {
          method: 'GET',
          credentials: 'include',
        });
        if (r.ok) {
          const contentType = r.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const j = await r.json();
            if (j?.authenticationToken) {
              console.log('[authAxios] ✓ Easy Auth token from /.auth/refresh');
              return j.authenticationToken;
            }
          }
        } else if (r.status === 403 || r.status === 404) {
          try {
            sessionStorage.setItem(AUTH_REFRESH_SKIP_KEY, '1');
          } catch (_) {
            /* ignore quota / private mode */
          }
        }
      }
    } catch (err) {
      // Silently ignore - optional endpoint
    }

    // Static Web Apps shape: [{ access_token }]
    console.log('[authAxios] Fetching from /.auth/me...');
    const res = await fetch('/.auth/me', { method: 'GET', credentials: 'include' });
    if (!res.ok) {
      console.log('[authAxios] /.auth/me returned status:', res.status);
      return null;
    }
    
    // Check if response is actually JSON (not HTML redirect page)
    const contentType = res.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // Response is not JSON (likely HTML redirect page) - Easy Auth exists but user not authenticated
      console.log('[authAxios] /.auth/me returned non-JSON (likely HTML redirect), user may not be authenticated');
      return null;
    }
    
    const json = await res.json();
    if (Array.isArray(json) && json[0]?.access_token) {
      console.log('[authAxios] ✓ Easy Auth token found in array format');
      return json[0].access_token;
    }
    // App Service older shapes sometimes surface tokens under identities array
    if (json?.clientPrincipal?.identities) {
      const any = json.clientPrincipal.identities.find((i) => i.access_token);
      if (any?.access_token) {
        console.log('[authAxios] ✓ Easy Auth token found in identities array');
        return any.access_token;
      }
    }

    console.log('[authAxios] No token found in /.auth/me response (cookies will be used for same-origin)');
    return null;
  } catch (err) {
    // Silently handle JSON parse errors (likely HTML response)
    if (err instanceof SyntaxError && err.message.includes('JSON')) {
      // Expected when /.auth/me returns HTML instead of JSON
      console.log('[authAxios] /.auth/me returned HTML instead of JSON (user may need to authenticate)');
      return null;
    }
    console.error('[authAxios] ✗ Failed to fetch Easy Auth token:', err);
    return null;
  }
};

// Fetch MSAL token
const fetchMsalToken = async () => {
  try {
    console.log('[authAxios] Fetching MSAL token...');
    const msal = await getMsalInstance();
    if (!msal) {
      console.warn('[authAxios] ✗ MSAL instance not available');
      return null;
    }

    // Ensure MSAL is initialized
    if (!msal.getAllAccounts || typeof msal.getAllAccounts !== 'function') {
      try {
        console.log('[authAxios] Initializing MSAL instance...');
        await msal.initialize();
      } catch (initErr) {
        console.warn('[authAxios] ✗ Failed to initialize MSAL:', initErr);
        return null;
      }
    }

    const accounts = msal.getAllAccounts();
    console.log('[authAxios] MSAL accounts in cache:', accounts.length);
    if (accounts.length === 0) {
      console.warn('[authAxios] ✗ No MSAL accounts found in cache');
      return null;
    }
    
    console.log('[authAxios] Using account:', accounts[0].username);

    try {
      // Check if API scopes are configured (and for a different app)
      // The config already filters out self-referencing scopes, so if apiScopes has items,
      // they're for a different API app registration
      const { apiScopes, apiTokenRequest } = await import('../config/msalConfig');
      
      // If API scopes are configured (for a different app), request an access token
      if (apiScopes && apiScopes.length > 0) {
        const apiRequest = apiTokenRequest(accounts[0]);
        if (apiRequest) {
          console.log('[authAxios] Requesting API access token with scopes:', apiRequest.scopes);
          const tokenResponse = await msal.acquireTokenSilent(apiRequest);
          return tokenResponse.accessToken; // Access token for the API
        }
      }
      
      // Otherwise, use the ID token for API authentication
      // This is the correct approach when frontend and backend use the same app registration
      // ID token is signed by Azure AD and can be validated by the backend
      console.log('[authAxios] Requesting ID token for API authentication (same app registration)');
      
      try {
        const tokenResponse = await msal.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
          forceRefresh: false, // Let MSAL handle refresh automatically
        });
        
        // Return ID token - this is what the backend validates for user authentication
        const idToken = tokenResponse.idToken || tokenResponse.accessToken;
        if (!idToken) {
          console.warn('[authAxios] ✗ Token response missing both idToken and accessToken');
          return null;
        }
        
        // Check if token is expired (MSAL should handle this, but double-check)
        try {
          const payload = JSON.parse(atob(idToken.split('.')[1]));
          const expiry = payload.exp * 1000; // Convert to milliseconds
          const now = Date.now();
          if (expiry < now) {
            console.warn('[authAxios] ⚠ Token is expired, attempting refresh...', {
              expiredAt: new Date(expiry).toISOString(),
              now: new Date(now).toISOString(),
            });
            // Try to force refresh
            const refreshedResponse = await msal.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
              forceRefresh: true,
            });
            const refreshedToken = refreshedResponse.idToken || refreshedResponse.accessToken;
            if (refreshedToken) {
              console.log('[authAxios] ✓ Token refreshed successfully');
              return refreshedToken;
            }
          }
        } catch (parseErr) {
          // If we can't parse the token, just use it (MSAL should have validated it)
          console.log('[authAxios] Could not parse token expiry, trusting MSAL validation');
        }
        
        console.log('[authAxios] ✓ ID token acquired successfully');
        return idToken;
      } catch (tokenError) {
        // If silent acquisition fails, check if it's an expiry issue
        if (tokenError.errorCode === 'token_expired' || tokenError.message?.includes('expired')) {
          console.warn('[authAxios] ⚠ Token expired, MSAL will require interaction');
        }
        throw tokenError; // Re-throw to be handled by outer catch
      }
    } catch (tokenError) {
      // Check if this is an interaction required error (token expired, consent needed, etc.)
      if (tokenError.errorCode === 'interaction_required' || 
          tokenError.errorCode === 'consent_required' ||
          tokenError.errorCode === 'login_required') {
        console.warn('[authAxios] ✗ Token acquisition requires interaction:', {
          errorCode: tokenError.errorCode,
          message: tokenError.message,
        });
        // Don't trigger interactive login here - let the 401 handler or AuthContext handle it
        // This prevents popups from happening in the request interceptor
      } else {
        console.warn('[authAxios] ✗ Silent token acquisition failed:', {
          errorCode: tokenError.errorCode,
          message: tokenError.message,
        });
      }
      return null;
    }
  } catch (err) {
    console.error('[authAxios] ✗ Failed to fetch MSAL token:', err);
    return null;
  }
};

// Detect which auth method is available
const detectAuthMethod = async () => {
  console.log('[authAxios] Detecting authentication method...', {
    useMsalEnabled: shouldUseMsal(),
    hasClientId: !!process.env.REACT_APP_AZURE_CLIENT_ID,
  });

  // If MSAL is explicitly enabled, prioritize it
  if (shouldUseMsal()) {
    console.log('[authAxios] MSAL is enabled, attempting to fetch MSAL token...');
    const token = await fetchMsalToken();
    if (token) {
      console.log('[authAxios] ✓ MSAL token acquired, using MSAL authentication');
      return { method: 'msal', token };
    }
    // If MSAL is enabled but token fetch failed, return msal method to trigger login
    console.log('[authAxios] MSAL enabled but token fetch failed, will trigger login');
    return { method: 'msal', token: null };
  }

  // Otherwise, use Easy Auth (default for Azure App Service)
  try {
    console.log('[authAxios] Checking for Easy Auth endpoint...');
    const res = await fetch('/.auth/me', { method: 'HEAD', credentials: 'include' });
    if (res.ok) {
      console.log('[authAxios] ✓ Easy Auth endpoint found, attempting to fetch token...');
      // Easy Auth endpoint exists - try to get token
      const token = await fetchEasyAuthToken();
      if (token) {
        console.log('[authAxios] ✓ Easy Auth token acquired');
        return { method: 'easyauth', token };
      }
      // Even if token fetch fails, Easy Auth is available (cookies work for same-origin)
      console.log('[authAxios] Easy Auth available but no token (will use cookies for same-origin)');
      return { method: 'easyauth', token: null };
    } else {
      console.log('[authAxios] Easy Auth endpoint returned status:', res.status);
    }
  } catch (err) {
    console.log('[authAxios] Easy Auth endpoint check failed:', err.message);
  }

  console.warn('[authAxios] ✗ No authentication method detected');
  return { method: null, token: null };
};

// Fetch access token - tries both methods
const fetchAccessToken = async () => {
  if (tokenPromise) return tokenPromise;
  
  tokenPromise = (async () => {
    try {
      const { token } = await detectAuthMethod();
      return token;
    } finally {
      tokenPromise = null;
    }
  })();
  
  return tokenPromise;
};

const authAxios = axios.create({ withCredentials: true });

authAxios.interceptors.request.use(
  async (config) => {
    // Which of our own API path prefixes is this request for? Used to decide
    // whether a bearer may be attached — we must never send an auth credential
    // to a foreign origin (e.g. the Azure blob/SAS image fetch, whose auth is
    // already in the URL). Computed up front so both the embed and non-embed
    // paths share one definition.
    const rawUrl = String(config.url || '');
    let pathname = rawUrl;
    try {
      // Absolute cross-origin URLs (blob/SAS) parse to their OWN pathname, which
      // is not one of our API prefixes → isSameOriginApi false → no bearer.
      pathname = new URL(rawUrl, window.location.origin).pathname;
    } catch (_) {
      // ignore URL parse issues; fallback to rawUrl
    }
    const isSameOriginApi =
      pathname.startsWith('/api') ||
      pathname.startsWith('/uploadImage') ||
      pathname.startsWith('/deleteImage') ||
      pathname.startsWith('/getImages') ||
      pathname.startsWith('/sas');

    // Embed mode: skip Easy Auth/MSAL detection — the parent frame (Tango) is
    // the only auth source, so force the Bearer path (no cookie session). But
    // ONLY for our own API: never stamp the Vault bridge token onto a
    // cross-origin request (the SAS/blob fetch runs through this instance too).
    if (isEmbedMode()) {
      const token = getEmbedToken();
      if (token && isSameOriginApi) {
        config.headers.Authorization = `Bearer ${token}`;
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
      } else if (!token) {
        console.warn('[authAxios] Embed mode but no bridge token yet — request may 401');
      }
      return config;
    }

    try {
      // Detect which auth method is being used
      const authInfo = await detectAuthMethod();
      const isEasyAuth = authInfo.method === 'easyauth';
      const isMsal = authInfo.method === 'msal';

      console.log('[authAxios] Preparing request', {
        method: config.method,
        url: pathname,
        authMethod: authInfo.method,
        isSameOriginApi,
      });

      // Always include cookies for Easy Auth (may be used for session)
      if (isEasyAuth) {
        config.withCredentials = true;
      }

      // For MSAL: always use Bearer token (no cookie support)
      if (isMsal) {
        const token = await fetchAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          console.log('[authAxios] ✓ Added Bearer token to request (MSAL)', { tokenPreview: token.substring(0, 20) + '...' });
        } else {
          // No token available - check if this is a critical request or if we should fail fast
          // Don't send requests without tokens if we know auth is required
          console.warn('[authAxios] ⚠ MSAL configured but no token available - request may fail with 401');
          // Still send the request - let the backend return 401 and let RequireAuth handle redirect
          // This prevents auto-login popups from happening in the interceptor
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
        }
      }
      // For Easy Auth: use cookies for same-origin, tokens for cross-origin
      else if (isEasyAuth) {
        // On Azure App Service with Easy Auth, cookies authenticate same-origin /api/*.
        // Supplying Authorization can cause 401/403 if the token audience doesn't match.
        // Therefore:
        // - For same-origin API calls: rely on cookies only
        // - For non-API calls (cross-service): attach a bearer token when available
        if (!isSameOriginApi) {
          const token = await fetchAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('[authAxios] ✓ Added Bearer token to request (Easy Auth, cross-origin)', { tokenPreview: token.substring(0, 20) + '...' });
          }
        } else {
          // Same-origin API calls (Easy Auth):
          // - Rely on cookie session
          // - Do NOT force Authorization header
          if (config.headers && 'Authorization' in config.headers) {
            delete config.headers.Authorization;
          }
          // Hint XHR semantics to platform CSRF
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          // Ensure no accidental legacy header is sent
          if (config.headers && 'X-ZUMO-AUTH' in config.headers) {
            delete config.headers['X-ZUMO-AUTH'];
          }
          console.log('[authAxios] ✓ Using cookies for authentication (Easy Auth, same-origin)');
        }
      }
      // If no auth method detected but token is available, try to use it
      else {
        const token = await fetchAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('[authAxios] ✓ Added Bearer token to request (fallback)', { tokenPreview: token.substring(0, 20) + '...' });
        } else {
          console.warn('[authAxios] ⚠ No auth method and no token available');
        }
      }

      return config;
    } catch (err) {
      console.error('[authAxios] Token acquisition error:', err);
      tokenPromise = null;
      return config;
    }
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401, log the error and let it propagate
// Don't try to handle login here - let AuthContext/RequireAuth handle it
// This prevents popup login attempts that the user didn't initiate
authAxios.interceptors.response.use(
  (res) => {
    // Log successful responses for debugging (only in dev or when needed)
    if (process.env.NODE_ENV === 'development') {
      console.log('[authAxios] ✓ Request successful', {
        method: res.config.method,
        url: res.config.url,
        status: res.status,
      });
    }
    return res;
  },
  async (error) => {
    const status = error?.response?.status;
    const config = error?.config;
    
    if (status === 401) {
      console.warn('[authAxios] ✗ 401 Unauthorized', {
        method: config?.method,
        url: config?.url,
        hasAuthHeader: !!config?.headers?.Authorization,
        authMethod: config?.authMethod || 'unknown',
        message: 'Token may be expired or missing',
      });
      
      // Clear token cache to force refresh on next request
      tokenPromise = null;
      console.log('[authAxios] Cleared token cache - will attempt fresh token on next request');
      
      // Let the error propagate - AuthContext or RequireAuth will handle redirecting to login
      // Don't try to login automatically here to avoid unwanted popups
    } else if (status === 403) {
      console.warn('[authAxios] ✗ 403 Forbidden', {
        method: config?.method,
        url: config?.url,
        message: 'User may not have required permissions',
      });
    } else if (error?.message) {
      console.error('[authAxios] ✗ Request failed', {
        method: config?.method,
        url: config?.url,
        status,
        message: error.message,
      });
    }
    return Promise.reject(error);
  }
);

export default authAxios;
